//React component for the "StationsPage" page i.e. the big page that lists all of the news stations
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import StationsPageTableBasic from './StationsPageTableBasic.js';
import { StationsPageHeader } from './StationsPageHeader.js';
import { getColumnHeadersBasic } from './getStationsPageTableColumnHeaders.js';
import { fetchChannelDataRows } from '../../utilities/apiRequests.js';
import { getLiveStatus } from "../../utilities/apiRequests.js";
import { Grid2 } from '@mui/material';
import HeaderBar from '../Shared/HeaderBar/HeaderBar.js';
import NavigationBar from "../Shared/NavigationBar/NavigationBar.js"
import AddRowModal from './AddRowModal';

export default function StationsPage() {

    const [sortConfig, setSortConfig] = useState({ field: "live", direction: "asc" });
    const [originalRows, setOriginalRows] = useState([]);
    const [shownRows, setShownRows] = useState([]);
    const [search, setSearch] = useState("");
    const { getAccessTokenSilently } = useAuth0();
    const columnHeaders = useRef(getColumnHeadersBasic());
    let topPadding = "0.5em";
    const liveStatusMapRef = useRef({});
    const [modalOpen, setModalOpen] = useState(false);


    const loadInitialRows = useCallback(async () => {
        try {
            const accessToken = await getAccessTokenSilently({
                authorizationParams: { scope: "read:channels" },
            });
            const fields = columnHeaders.current.map((col) => col.field);
            
            // First get channel data and show it immediately (with all isLive = false)
            const fetchedRows = await fetchChannelDataRows(accessToken, fields);
            
            // Show initial rows immediately without live status
            const initialRows = fetchedRows.map(row => ({
                ...row,
                isLive: false,
            }));
            setOriginalRows(initialRows);
            setShownRows(initialRows);
            
            // Then fetch live statuses in the background and update
            const statusMap = await fetchAllLiveStatuses(accessToken, fetchedRows);
            liveStatusMapRef.current = statusMap;

            const mergedRows = fetchedRows.map(row => ({
                ...row,
                isLive: statusMap[row.channel_id] || false,
            }));

            setOriginalRows(mergedRows);
            // default sort once (live asc)
            const sorted = sortRowsFromData(mergedRows, "live", "asc");
            setShownRows(sorted);
        } catch (error) {
            console.error("Error fetching rows:", error);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        loadInitialRows();
    }, [loadInitialRows]);

    const sortRowsFromData = (rows, field, direction = "asc") => {
        return [...rows].sort((rowA, rowB) => {
            if (field === "live") {
                if (rowA.isLive === rowB.isLive) return 0;
                if (direction === "asc") return rowA.isLive ? -1 : 1;
                if (direction === "desc") return rowA.isLive ? 1 : -1;
                return 0;
            }
            const valA = rowA[field] || '';
            const valB = rowB[field] || '';
            const comparison = valA.localeCompare(valB);
            if (direction === "asc") return comparison;
            if (direction === "desc") return -comparison;
            return 0;
        });
    };

    const filterRows = (rows, searchTerm) => {
        const lower = searchTerm.toLowerCase();
        return rows.filter((row) =>
            row.call_sign?.toLowerCase().includes(lower) ||
            row.affiliation?.toLowerCase().includes(lower) ||
            row.market?.toLowerCase().includes(lower)
        );
    };

    // Handle sort directly (without implicitly relying on useEffect or useSort)
    const handleSort = (field) => {
        let newSortConfig;

        if (field === "live") {
            newSortConfig = {
                field,
                direction: sortConfig.field === 'live' && sortConfig.direction === "asc" ? "desc" : "asc"
            };
        }
        else {
            if (sortConfig.field !== field) newSortConfig = { field, direction: "asc" };
            else if (sortConfig.direction === "asc") newSortConfig = { field, direction: "desc" };
            else if (sortConfig.direction === "desc") newSortConfig = { field, direction: null };
            else newSortConfig = { field, direction: "asc" };
        }

        setSortConfig(newSortConfig);
        const base = search ? filterRows(originalRows, search) : [...originalRows];
        const sorted = newSortConfig.field ? sortRowsFromData(base, newSortConfig.field, newSortConfig.direction) : base;
        setShownRows(sorted);

    };

    const handleSearch = (value) => {
        setSearch(value);
        const filtered = filterRows(originalRows, value);
        const sorted = sortConfig.field ? sortRowsFromData(filtered, sortConfig.field) : filtered;
        setShownRows(sorted);
    };

    async function fetchAllLiveStatuses(accessToken, rows) {
        const statusMap = {};
        await Promise.all(rows.map(async (row) => {
            try {
                const status = await getLiveStatus(accessToken, row.channel_id);
                statusMap[row.channel_id] = status.isLive;
            } catch (e) {
                statusMap[row.channel_id] = false;
            }
        }));
        return statusMap;
    };

    function handleAddRow() {
        setModalOpen(true);
    }

    function handleCloseModal() {
        setModalOpen(false);
    }

    return (
        <>
            <HeaderBar page={"StationsPage"} ></HeaderBar>
            <Grid2 container={true} justifyContent={'center'} alignItems={'center'} direction={'column'} paddingBottom={'100px'} id={"StationsPageBasicGrid"} >
                <StationsPageHeader
                    sortConfig={sortConfig}
                    sortRows={handleSort}
                    search={search}
                    setSearch={handleSearch}
                    topPadding={topPadding}
                    onAddRow={handleAddRow}
                />
                <StationsPageTableBasic columnHeaders={columnHeaders.current} shownRows={shownRows} />
            </Grid2>
            <NavigationBar></NavigationBar>
            <AddRowModal open={modalOpen} onClose={handleCloseModal} />
        </>
    )
}
